import { ApiClient, createResources } from '@pedigreesmart/api-client'
import {
  can,
  defaultSpecies,
  isMultiSpecies,
  type PermissionName,
  type SessionContext,
  type SpeciesContext,
} from '@pedigreesmart/core'
import { createTranslator, type Translator } from '@pedigreesmart/i18n'
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react'

import { createTokenStore } from './tokens.js'

interface SessionValue {
  context: SessionContext | null
  species: SpeciesContext | null
  multiSpecies: boolean
  api: ApiClient
  resources: ReturnType<typeof createResources>
  t: Translator
  can: (permission: PermissionName) => boolean
  selectSpecies: (code: string) => void
  login: (email: string, password: string) => Promise<void>
  switchTenant: (tenantId: number) => Promise<void>
  logout: () => Promise<void>
}

const SessionCtx = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<SessionContext | null>(null)
  const [speciesCode, setSpeciesCode] = useState<string | null>(null)

  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: `${import.meta.env.VITE_API_BASE ?? ''}/api/v1`,
        tokens: createTokenStore(),
        onSessionExpired: () => {
          setContext(null)
          setSpeciesCode(null)
        },
      }),
    [],
  )

  const resources = useMemo(() => createResources(api), [api])

  // The active species drives every label and every attribute form. Tenants
  // with a single species never choose: it resolves to their only one.
  const species = useMemo(() => {
    if (!context) return null
    if (speciesCode) {
      return context.species.find((s) => s.code === speciesCode) ?? defaultSpecies(context)
    }

    return defaultSpecies(context)
  }, [context, speciesCode])

  const t = useMemo(
    () => createTranslator(context?.user ? 'it' : 'it', species),
    [context, species],
  )

  const login = useCallback(
    async (email: string, password: string) => {
      setContext(await api.login(email, password))
      setSpeciesCode(null)
    },
    [api],
  )

  const switchTenant = useCallback(
    async (tenantId: number) => {
      setContext(await api.switchTenant(tenantId))
      setSpeciesCode(null)
    },
    [api],
  )

  const logout = useCallback(async () => {
    await api.logout()
    setContext(null)
    setSpeciesCode(null)
  }, [api])

  const value = useMemo<SessionValue>(
    () => ({
      context,
      species,
      multiSpecies: isMultiSpecies(context),
      api,
      resources,
      t,
      // Server-side voters are the real check; this only decides what to show.
      can: (permission) => can(context?.permissions ?? [], permission),
      selectSpecies: setSpeciesCode,
      login,
      switchTenant,
      logout,
    }),
    [context, species, api, resources, t, login, switchTenant, logout],
  )

  return <SessionCtx value={value}>{children}</SessionCtx>
}

export function useSession(): SessionValue {
  const value = use(SessionCtx)

  if (!value) {
    throw new Error('useSession must be used inside <SessionProvider>.')
  }

  return value
}

/** Resolves a domain term for the active species: «Cucciolo» or «Puledro». */
export function useTerm(key: string): string {
  return useSession().t(`species:${key}`)
}
